import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader } from "../components/ui-bits";
import { Save, Plus, Trash2, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Navigate } from "react-router-dom";

/* -------------------------------------------------- */
/*  Primitives                                        */
/* -------------------------------------------------- */

function Section({ title, subtitle, defaultOpen = true, children, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card-dark p-6 md:p-8 mb-6" data-testid={testId}>
      <button type="button" onClick={() => setOpen((x) => !x)}
              className="w-full flex items-start justify-between mb-4 text-left">
        <div>
          <h2 className="h-editorial text-xl">{title}</h2>
          {subtitle && <p className="text-[11px] text-[#737373] mt-1">{subtitle}</p>}
        </div>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && <div>{children}</div>}
    </section>
  );
}

function ListEditor({ items, setItems, columns, testIdPrefix, blankItem }) {
  const add = () => setItems([...(items || []), { ...blankItem }]);
  const update = (i, key, val) => {
    const next = [...items]; next[i] = { ...next[i], [key]: val }; setItems(next);
  };
  const remove = (i) => { const next = [...items]; next.splice(i, 1); setItems(next); };
  return (
    <div>
      <div className="hidden md:grid gap-2 mb-2 text-[10px] uppercase tracking-widest text-[#737373]"
           style={{ gridTemplateColumns: `${columns.map((c) => c.width || "1fr").join(" ")} 40px` }}>
        {columns.map((c) => <div key={c.key}>{c.label}</div>)}<div />
      </div>
      <div className="space-y-2">
        {(items || []).map((it, i) => (
          <div key={i} className="grid gap-2"
               style={{ gridTemplateColumns: `${columns.map((c) => c.width || "1fr").join(" ")} 40px` }}
               data-testid={`${testIdPrefix}-row-${i}`}>
            {columns.map((c) => (
              <input key={c.key} type={c.type || "text"} step={c.type === "number" ? "any" : undefined}
                     placeholder={c.label} value={it[c.key] ?? ""}
                     onChange={(e) => update(i, c.key, c.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                     className="input-dark w-full text-xs"
                     data-testid={`${testIdPrefix}-${i}-${c.key}`} />
            ))}
            <button onClick={() => remove(i)} className="text-[#737373] hover:text-red-600" data-testid={`${testIdPrefix}-remove-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <button onClick={add} className="btn-ghost mt-3 inline-flex items-center gap-2 text-xs" data-testid={`${testIdPrefix}-add`}>
        <Plus size={12} /> Add row
      </button>
    </div>
  );
}

/* JSON editor for the deeply nested tables (taxonomy, modifiers with ranges,
 * quantity_multipliers). Faster than building bespoke editors for tables that
 * are seeded from the MMS spec and rarely edited. */
function JsonEditor({ value, setValue, testId, rows = 12 }) {
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => { setDraft(JSON.stringify(value ?? [], null, 2)); }, [value]);
  const commit = () => {
    try {
      const parsed = JSON.parse(draft || "null");
      setValue(parsed); setErr("");
    } catch (e) { setErr(e.message); }
  };
  return (
    <div>
      <textarea rows={rows} className="input-dark w-full font-mono text-[11px]" value={draft}
                onChange={(e) => setDraft(e.target.value)} onBlur={commit} data-testid={testId} />
      {err && <div className="text-red-600 text-[11px] mt-1">JSON error: {err}</div>}
      <button type="button" onClick={commit} className="btn-ghost mt-2 text-[10px] uppercase tracking-widest">Apply JSON</button>
    </div>
  );
}

/* -------------------------------------------------- */
/*  Page                                              */
/* -------------------------------------------------- */

export default function MmsPricingAdminPage() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/mms/pricing-config"); setCfg(data); }
      catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  if (user && !user.is_super_admin) return <Navigate to="/dashboard" replace />;

  if (loading || !cfg) return <div className="flex items-center gap-2 text-xs text-[#737373]"><Loader2 size={14} className="animate-spin" /> Loading pricing config…</div>;

  const patch = (path, val) => setCfg((prev) => {
    const next = JSON.parse(JSON.stringify(prev));
    const keys = path.split(".");
    let ptr = next;
    for (let i = 0; i < keys.length - 1; i++) ptr = ptr[keys[i]] = ptr[keys[i]] || {};
    ptr[keys[keys.length - 1]] = val;
    return next;
  });

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.put("/mms/pricing-config", cfg);
      setCfg(data); toast.success("Pricing config saved");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="mms-pricing-admin-page">
      <PageHeader
        eyebrow="MMS Studio"
        title="MMS Pricing Knowledge Base"
        description="Configure the universal MMS pricing engine — labour rate, taxonomy, hour tables, multipliers and AI methodology."
        action={
          <button onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2 disabled:opacity-50" data-testid="mms-pricing-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Configuration
          </button>
        }
      />

      {err && <div className="mb-6 border border-red-600/40 bg-red-50 text-red-700 text-xs p-3">{err}</div>}

      {/* --- Basics --- */}
      <Section title="Basics" subtitle="Currency and MMS Studio labour rate." testId="mms-cfg-basics">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label-caps block mb-2">Currency (ISO)</label>
            <input className="input-dark w-full" value={cfg.currency || "EUR"} onChange={(e) => patch("currency", e.target.value.toUpperCase())} data-testid="mms-cfg-currency" />
          </div>
          <div>
            <label className="label-caps block mb-2">MMS labour rate (€/h)</label>
            <input type="number" step="0.5" className="input-dark w-full" value={cfg.hourly_rate ?? 20} onChange={(e) => patch("hourly_rate", Number(e.target.value))} data-testid="mms-cfg-hourly-rate" />
          </div>
        </div>
      </Section>

      {/* --- AI Engine --- */}
      <Section title="AI Pricing Engine — model & thresholds" subtitle="Which model runs vision analysis and how strict the confidence gates are." testId="mms-cfg-ai-model" defaultOpen={true}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="label-caps block mb-2">Provider</label>
            <select className="input-dark w-full" value={cfg.ai?.model_provider || "openai"} onChange={(e) => patch("ai.model_provider", e.target.value)} data-testid="mms-cfg-ai-provider">
              <option value="openai">openai</option><option value="anthropic">anthropic</option><option value="gemini">gemini</option>
            </select>
          </div>
          <div>
            <label className="label-caps block mb-2">Model</label>
            <input className="input-dark w-full" value={cfg.ai?.model_name || "gpt-5.4"} onChange={(e) => patch("ai.model_name", e.target.value)} data-testid="mms-cfg-ai-model-name" placeholder="gpt-5.4 · claude-sonnet-4-6 · gemini-3.1-pro-preview" />
          </div>
          <div>
            <label className="label-caps block mb-2">Warn threshold</label>
            <input type="number" step="0.05" min="0" max="1" className="input-dark w-full" value={cfg.ai?.confidence_warn ?? 0.7} onChange={(e) => patch("ai.confidence_warn", Number(e.target.value))} data-testid="mms-cfg-ai-warn" />
          </div>
          <div>
            <label className="label-caps block mb-2">Block threshold</label>
            <input type="number" step="0.05" min="0" max="1" className="input-dark w-full" value={cfg.ai?.confidence_block ?? 0.4} onChange={(e) => patch("ai.confidence_block", Number(e.target.value))} data-testid="mms-cfg-ai-block" />
          </div>
        </div>
      </Section>

      <Section title="AI System Prompt" subtitle="MMS Studio pricing methodology — how MMS reasons about garments." testId="mms-cfg-ai-prompt-section" defaultOpen={true}>
        <textarea rows={16} className="input-dark w-full font-mono text-xs" value={cfg.ai?.system_prompt || ""}
                  onChange={(e) => patch("ai.system_prompt", e.target.value)}
                  placeholder="Describe how MMS Studio prices garments: rules of thumb, examples, edge cases, hourly effort benchmarks…"
                  data-testid="mms-cfg-ai-prompt" />
      </Section>

      <Section title="AI Knowledge Base" subtitle="Short factual snippets appended to the system prompt." testId="mms-cfg-ai-kb-section">
        <ListEditor items={cfg.ai?.knowledge_snippets} setItems={(v) => patch("ai.knowledge_snippets", v)}
                    testIdPrefix="mms-cfg-ai-kb" blankItem={{ title: "", content: "" }}
                    columns={[{ key: "title", label: "Title", width: "1fr" }, { key: "content", label: "Content", width: "3fr" }]} />
      </Section>

      {/* --- Universal taxonomy --- */}
      <Section title="Universal taxonomy" subtitle="Categories → subcategories with base hours (pattern / sample / production per unit @ ~50pcs)." testId="mms-cfg-taxonomy" defaultOpen={false}>
        <p className="text-[11px] text-[#525252] mb-3">
          The MMS taxonomy is deep. Edit as JSON — each category has <code>{"{key, label, subcategories:[]}"}</code>
          and each subcategory <code>{"{key, label, hours:{pattern, sample, production_unit}}"}</code>.
        </p>
        <JsonEditor value={cfg.taxonomy} setValue={(v) => patch("taxonomy", v)} testId="mms-cfg-taxonomy-json" rows={18} />
      </Section>

      {/* --- Construction modifiers --- */}
      <Section title="Construction modifiers" subtitle="Pattern / sample / production hours per element (pockets, zippers, lining, etc.)." testId="mms-cfg-modifiers" defaultOpen={false}>
        <p className="text-[11px] text-[#525252] mb-3">
          Fixed modifiers: <code>{"{key, label, pattern, sample, production}"}</code>. Range modifiers:
          add <code>is_range: true</code> and use <code>pattern_range</code> / <code>sample_range</code> / <code>production_range</code>.
        </p>
        <JsonEditor value={cfg.construction_modifiers} setValue={(v) => patch("construction_modifiers", v)} testId="mms-cfg-modifiers-json" rows={16} />
      </Section>

      {/* --- Pattern sources --- */}
      <Section title="Pattern sources (§5)" subtitle="Rules for pattern provenance." testId="mms-cfg-pattern-sources" defaultOpen={false}>
        <JsonEditor value={cfg.pattern_sources} setValue={(v) => patch("pattern_sources", v)} testId="mms-cfg-pattern-sources-json" rows={12} />
      </Section>

      {/* --- Material handling --- */}
      <Section title="Material handling multipliers (§8)" subtitle="Applied to cutting + sewing hours." testId="mms-cfg-material-handling" defaultOpen={false}>
        <JsonEditor value={cfg.material_handling} setValue={(v) => patch("material_handling", v)} testId="mms-cfg-material-handling-json" rows={12} />
        <label className="label-caps block mt-4 mb-2">Multiple materials addition range (+% on handling)</label>
        <div className="grid grid-cols-2 gap-2">
          <input type="number" step="0.01" className="input-dark w-full" value={cfg.multiple_materials_addition_range?.[0] ?? 0.10}
                 onChange={(e) => patch("multiple_materials_addition_range", [Number(e.target.value), cfg.multiple_materials_addition_range?.[1] ?? 0.25])}
                 data-testid="mms-cfg-multi-mat-min" />
          <input type="number" step="0.01" className="input-dark w-full" value={cfg.multiple_materials_addition_range?.[1] ?? 0.25}
                 onChange={(e) => patch("multiple_materials_addition_range", [cfg.multiple_materials_addition_range?.[0] ?? 0.10, Number(e.target.value)])}
                 data-testid="mms-cfg-multi-mat-max" />
        </div>
      </Section>

      {/* --- Waste percentages --- */}
      <Section title="Waste percentages (§9)" subtitle="Applied to materials cost by fabric type." testId="mms-cfg-waste" defaultOpen={false}>
        <JsonEditor value={cfg.waste_percentages} setValue={(v) => patch("waste_percentages", v)} testId="mms-cfg-waste-json" rows={10} />
      </Section>

      {/* --- Accessories --- */}
      <Section title="Accessories catalog (§10)" subtitle="Zippers, buttons, hardware — each with unit_cost, application_time, setup, MOQ." testId="mms-cfg-accessories" defaultOpen={false}>
        <ListEditor items={cfg.accessories} setItems={(v) => patch("accessories", v)}
                    testIdPrefix="mms-cfg-accessory" blankItem={{ key: "", label: "", unit_cost: 0, quantity_default: 1, application_time: 0, setup_cost: 0, moq: 0 }}
                    columns={[
                      { key: "key", label: "Key" },
                      { key: "label", label: "Label" },
                      { key: "unit_cost", label: "Unit €", type: "number" },
                      { key: "quantity_default", label: "Qty/g.", type: "number" },
                      { key: "application_time", label: "Appl. h", type: "number" },
                      { key: "setup_cost", label: "Setup €", type: "number" },
                      { key: "moq", label: "MOQ", type: "number" },
                    ]} />
      </Section>

      {/* --- Processing techniques --- */}
      <Section title="Processing techniques (§11)" subtitle="Prints, embroidery, treatments." testId="mms-cfg-processing" defaultOpen={false}>
        <JsonEditor value={cfg.processing_techniques} setValue={(v) => patch("processing_techniques", v)} testId="mms-cfg-processing-json" rows={14} />
      </Section>

      {/* --- Manual work brackets --- */}
      <Section title="Manual work brackets (§7)" subtitle="Hours ranges for applications & patchwork." testId="mms-cfg-manual" defaultOpen={false}>
        <JsonEditor value={cfg.manual_work_brackets} setValue={(v) => patch("manual_work_brackets", v)} testId="mms-cfg-manual-json" rows={8} />
      </Section>

      {/* --- Quantity multipliers --- */}
      <Section title="Quantity multipliers (§12)" subtitle="Per-unit production multipliers by qty + complexity." testId="mms-cfg-qty" defaultOpen={false}>
        <ListEditor items={cfg.quantity_multipliers} setItems={(v) => patch("quantity_multipliers", v)}
                    testIdPrefix="mms-cfg-qty" blankItem={{ qty: 0, low: 1, medium: 1, high: 1, very_high: 1 }}
                    columns={[
                      { key: "qty", label: "Qty", type: "number" },
                      { key: "low", label: "Low", type: "number" },
                      { key: "medium", label: "Medium", type: "number" },
                      { key: "high", label: "High", type: "number" },
                      { key: "very_high", label: "V.High", type: "number" },
                    ]} />
      </Section>

      {/* --- Complexity levels --- */}
      <Section title="Complexity levels (§13)" testId="mms-cfg-complexity" defaultOpen={false}>
        <ListEditor items={cfg.complexity_levels} setItems={(v) => patch("complexity_levels", v)}
                    testIdPrefix="mms-cfg-complexity" blankItem={{ key: "", label: "" }}
                    columns={[{ key: "key", label: "Key" }, { key: "label", label: "Label" }]} />
      </Section>

      <div className="flex justify-end pt-4">
        <button onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2 disabled:opacity-50" data-testid="mms-pricing-save-bottom">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Configuration
        </button>
      </div>
    </div>
  );
}
